import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@wundo/ui';
import type { MeetingTopicJudgmentResponse } from '@wundo/shared/meetingTopicJudgment.ts';
import { formatShortDayMonthAndTime } from '@/components/agenda/meetingFormat.ts';
import { fetchMeetingTopicJudgment } from '@/lib/meetingPage.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingTopicJudgmentDialogProps {
  open: boolean;
  /** Whose meeting this is — the same user the rest of the page is read for. */
  userId: string;
  meetingId: string;
  onClose: () => void;
}

/**
 * Which topics of the gesprekstype the conversation really discussed, with the
 * fragment that shows it: for a superadmin checking the judgment behind the
 * admin dashboard. Read only when opened; a 404 reads as "not judged yet".
 */
export function MeetingTopicJudgmentDialog({
  open,
  userId,
  meetingId,
  onClose,
}: MeetingTopicJudgmentDialogProps) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['meetingTopicJudgment', userId, meetingId],
    queryFn: () => fetchMeetingTopicJudgment(userId, meetingId),
    enabled: open,
    retry: false,
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size='lg'>
        <DialogHeader>
          <DialogTitle>{m.meeting_topics_title()}</DialogTitle>
        </DialogHeader>

        <div className='max-h-96 overflow-y-auto px-5 py-4'>
          {isError && (
            <p className='text-sm text-ink-muted'>{m.meeting_topics_empty()}</p>
          )}

          {!isError && isPending && (
            <p className='text-sm text-ink-muted'>{m.meeting_topics_loading()}</p>
          )}

          {data && (
            <div className='flex flex-col gap-4'>
              <p className='text-sm text-ink-muted'>
                {m.meeting_topics_judged_at({
                  date: formatShortDayMonthAndTime(new Date(data.judged_at)),
                })}{' '}
                {judgedFrom(data)}
              </p>

              <ol className='flex flex-col gap-3.5'>
                {data.topics.map((topic) => (
                  <li key={topic.position} className='flex min-w-0 flex-col gap-1'>
                    <p className='flex items-center gap-2'>
                      <span className='text-sm font-semibold text-ink'>
                        {topic.title}
                      </span>
                      <Badge tone={topic.discussed ? 'success' : 'neutral'}>
                        {topic.discussed
                          ? m.meeting_topics_discussed()
                          : m.meeting_topics_not_discussed()}
                      </Badge>
                    </p>
                    {topic.evidence && (
                      <p className='text-sm leading-relaxed text-ink-secondary'>
                        “{topic.evidence}”
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={onClose}>
            {m.meeting_topics_close()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function judgedFrom(judgment: MeetingTopicJudgmentResponse): string {
  if (judgment.from_transcript && judgment.from_notes)
    return m.meeting_topics_from_both();
  if (judgment.from_notes) return m.meeting_topics_from_notes();
  return m.meeting_topics_from_transcript();
}
